import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BriefcaseIcon, LayoutDashboardIcon, PackageIcon, UsersIcon, SparklesIcon, BarChart3Icon, BotIcon, SettingsIcon, ArrowRightIcon, Share2Icon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL, BUSINESS_TYPE_LABEL } from './BusinessContext';
type ModuleCard = {
  icon: React.ElementType;
  title: string;
  description: string;
  accent: string;
};
export function BusinessHubWelcomeModal() {
  const {
    hub,
    markHubSeen
  } = useBusiness();
  const navigate = useNavigate();
  const open = Boolean(hub?.active && hub?.isNew);
  const handleStart = () => {
    markHubSeen();
    navigate('/business');
  };
  if (!hub) return null;
  const catalogLabel = CATALOG_LABEL[hub.type] ?? 'Catalog';
  const typeLabel = BUSINESS_TYPE_LABEL[hub.type] ?? 'Business';
  const modules: ModuleCard[] = [{
    icon: LayoutDashboardIcon,
    title: 'Dashboard',
    description: 'See performance, leads and tasks at a glance.',
    accent: 'text-primary-indigo bg-primary-indigo/10'
  }, {
    icon: PackageIcon,
    title: catalogLabel,
    description: 'Add inventory by chat, file or link — auto-categorized.',
    accent: 'text-ai-purple bg-ai-purple/10'
  }, {
    icon: UsersIcon,
    title: 'Leads',
    description: 'Capture and track every buyer enquiry in one place.',
    accent: 'text-electric-cyan bg-electric-cyan/10'
  }, {
    icon: Share2Icon,
    title: 'Your shop link',
    description: 'Get your own shareable shop link to send to customers.',
    accent: 'text-ai-purple bg-ai-purple/10'
  }, {
    icon: BotIcon,
    title: 'AI Agents',
    description: 'Deploy agents that work leads while you sleep.',
    accent: 'text-electric-cyan bg-electric-cyan/10'
  }];
  return <AnimatePresence data-id="element-619">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} role="dialog" aria-modal="true" aria-labelledby="hub-welcome-title" data-id="element-620">
          <div className="absolute inset-0 bg-dark-navy/60 backdrop-blur-sm" data-id="element-621" />

          <motion.div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden" initial={{
        opacity: 0,
        scale: 0.94,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 26
      }} data-id="element-622">
            {/* Header */}
            <div className="relative px-8 pt-8 pb-6 bg-gradient-to-br from-primary-indigo to-ai-purple text-white overflow-hidden" data-id="element-623">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" data-id="element-624" />
              <div className="relative flex items-center gap-4" data-id="element-625">
                <motion.div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shadow-inner" initial={{
              rotate: -8,
              scale: 0.8
            }} animate={{
              rotate: 0,
              scale: 1
            }} transition={{
              delay: 0.1,
              type: 'spring',
              stiffness: 200
            }} data-id="element-626">
                  <BriefcaseIcon className="w-7 h-7" data-id="element-627" />
                </motion.div>
                <div data-id="element-628">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70" data-id="element-629">
                    {typeLabel} workspace
                  </p>
                  <h2 id="hub-welcome-title" className="text-2xl font-bold leading-tight" data-id="element-630">
                    Welcome to your Business Hub
                    {hub.name ? `, ${hub.name}` : ''}
                  </h2>
                </div>
              </div>
              <p className="relative mt-4 text-sm text-white/80 max-w-lg" data-id="element-631">
                Everything you need to sell smarter is now in your sidebar.
                Here's a quick tour of what each module can do for you.
              </p>
            </div>

            {/* Module grid */}
            <div className="px-8 py-6" data-id="element-632">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-633">
                {modules.map((m, i) => <motion.div key={m.title} className="flex items-start gap-3 p-3.5 rounded-2xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.15 + i * 0.05
            }} data-id="element-634">
                    <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${m.accent}`} data-id="element-635">
                      <m.icon className="w-5 h-5" data-id="element-636" />
                    </div>
                    <div className="min-w-0" data-id="element-637">
                      <p className="text-sm font-semibold text-slate-900" data-id="element-638">
                        {m.title}
                      </p>
                      <p className="text-xs text-slate-500 leading-snug mt-0.5" data-id="element-639">
                        {m.description}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 pb-8 flex flex-col sm:flex-row items-center gap-3 sm:justify-between" data-id="element-640">
              <p className="text-xs text-slate-400 flex items-center gap-1.5" data-id="element-641">
                <SettingsIcon className="w-3.5 h-3.5" data-id="element-642" />
                You can fine-tune everything later in Settings.
              </p>
              <button onClick={handleStart} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white font-semibold shadow-lg shadow-primary-indigo/25 hover:shadow-xl hover:scale-[1.02] active:scale-[0.99] transition-all" data-id="element-643">
                Get started
                <ArrowRightIcon className="w-4 h-4" data-id="element-644" />
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}